import { Box, Button, Grid, TextField, Typography } from '@mui/material'
import { addItemToBox, getItemsInBox } from 'api/box'
import { SnackbarSeverity } from 'constant'
import { BoxOrderContext } from 'contexts/BoxOrderContextProvider'
import { LoadingContext } from 'contexts/LoadingContextProvider'
import { OrderContext } from 'contexts/OrderContextProvider'
import { SnackContext } from 'contexts/SnackContextProvider'
import useDailyOrder from 'hooks/useDailyOrder'
import { OrderItem } from 'models'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { setElementToTop } from 'utils'

const ScanItem = () => {
  const { company } = useParams()
  const { handleOpenSnackbar } = useContext(SnackContext)
  const [itemCode, setItemCode] = useState('')
  const [error, setError] = useState(false)
  const { boxItems, selectedRack, setBoxCode, selectedClass, boxCode } =
    useContext(BoxOrderContext)
  const {
    selectedLocationId,
    selectedDailyOrderBatchIndex,
    filteredDailyOrdersByClass
  } = useContext(OrderContext)

  const getBatchIndexByLabel = (label?: string | null): number => {
    const normalized = `${label || ''}`.trim().toUpperCase()
    if (!/^[A-Z]+$/.test(normalized)) return Number.MAX_SAFE_INTEGER
    let result = 0
    for (let i = 0; i < normalized.length; i++) {
      result = result * 26 + (normalized.charCodeAt(i) - 64)
    }
    return result - 1
  }
  const getBatchLabels = (orders: any[]) =>
    Array.from(
      new Set(
        orders
          .map(order => `${order?.batchLabel || ''}`.trim().toUpperCase())
          .filter((label: string) => label.length > 0)
      )
    ).sort((a, b) => getBatchIndexByLabel(a) - getBatchIndexByLabel(b))

  const selectedBatchLabel = useMemo(() => {
    if (
      !filteredDailyOrdersByClass?.length ||
      selectedDailyOrderBatchIndex === undefined
    ) {
      return ''
    }
    const labels = getBatchLabels(filteredDailyOrdersByClass)
    return labels[selectedDailyOrderBatchIndex] || ''
  }, [filteredDailyOrdersByClass, selectedDailyOrderBatchIndex])
  const { loading, setLoading } = useContext(LoadingContext)
  const currentItems =
    boxItems
      .find(item => item.class === selectedClass)
      ?.racks.find((orderRack: any) => orderRack.rack === selectedRack)
      ?.items || []
  const [allItems, setAllItems] = useState<any[]>([])
  const [itemsInSelectedBox, setItemsInSelectedBox] = useState<any[]>([])
  const { getBoxItemBackgroundColor } = useDailyOrder()
  const itemInputRef = useRef<HTMLInputElement | null>(null)

  const focusItemInput = () => {
    requestAnimationFrame(() => {
      itemInputRef.current?.focus()
    })
  }

  const handleScanItem = async () => {
    setLoading(true)
    try {
      const scannedItem = allItems.find(
        (item: any) =>
          item.name === itemCode.trim() || item.upcCode === itemCode.trim()
      )
      if (!scannedItem) {
        handleOpenSnackbar(
          SnackbarSeverity.Error,
          'Item not found, please check the item code'
        )
        return
      }
      if (scannedItem.boxQuantity < scannedItem.quantityRemaining) {
        const result = await addItemToBox(
          `${company}`,
          Number(selectedLocationId),
          `${boxCode}`,
          scannedItem.name
        )
        if (result.data.success) {
          const addedItem = result.data.item
          const newAllItems = allItems.map((item: any) => {
            if (item.name === addedItem.name) {
              return {
                ...item,
                boxQuantity: (item.boxQuantity || 0) + 1
              }
            }
            return item
          })
          const updatedItems = setElementToTop(
            newAllItems,
            newAllItems.findIndex((item: any) => item.name === addedItem.name)
          )
          setAllItems(updatedItems)
          handleOpenSnackbar(SnackbarSeverity.Success, 'Item added to box')
        } else {
          handleOpenSnackbar(
            SnackbarSeverity.Error,
            'Item not found, please check the item code'
          )
          return
        }
      } else {
        handleOpenSnackbar(SnackbarSeverity.Error, 'Item already added to box')
      }
    } catch (error) {
      handleOpenSnackbar(
        SnackbarSeverity.Error,
        'Server error, please try again later'
      )
    } finally {
      setItemCode('')
      setLoading(false)
      focusItemInput()
    }
  }
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !loading) {
      handleScanItem()
    }
  }

  const fetchExistingItems = async () => {
    let mergedItems: any[] = []
    let itemsInThisBox: any[] = []
    if (selectedLocationId) {
      try {
        const locationId = Number(selectedLocationId)
        const [batchBoxItemsResult, currentBoxItemsResult] = await Promise.all([
          selectedBatchLabel
            ? getItemsInBox(
                `${company}`,
                locationId,
                undefined,
                selectedBatchLabel
              )
            : Promise.resolve({ data: { success: true, boxItems: [] } }),
          boxCode
            ? getItemsInBox(`${company}`, locationId, boxCode)
            : Promise.resolve({ data: { success: false, boxItems: [] } })
        ])
        if (batchBoxItemsResult.data.success) {
          const allBoxItems = batchBoxItemsResult.data.boxItems || []
          const totalQuantityByName = new Map<string, number>()
          allBoxItems.forEach((it: any) => {
            const name = (it.name ?? '').toString().trim()
            const qty = Number(it.quantity) || 0
            totalQuantityByName.set(
              name,
              (totalQuantityByName.get(name) ?? 0) + qty
            )
          })
          mergedItems = currentItems.map((it: any) => {
            const name = (it.name ?? '').toString().trim()
            const boxQty = Number(totalQuantityByName.get(name) ?? 0)
            return { ...it, boxQuantity: boxQty }
          })
        }
        if (boxCode && currentBoxItemsResult.data.success) {
          itemsInThisBox = currentBoxItemsResult.data.boxItems || []
        }
      } catch {
        mergedItems = currentItems.map((it: any) => ({
          ...it,
          boxQuantity: Number(it.boxQuantity) || 0
        }))
        handleOpenSnackbar(
          SnackbarSeverity.Error,
          'Failed to load box items, showing cached list'
        )
      }
    }
    const byBinAsc = (a: any, b: any) =>
      (a?.bin ?? '')
        .toString()
        .localeCompare((b?.bin ?? '').toString(), undefined, {
          sensitivity: 'base'
        })
    const sortedByBin = mergedItems.slice().sort(byBinAsc)
    const boxQty = (it: any) => Number(it.boxQuantity) || 0
    const qtyRemaining = (it: any) => Number(it.quantityRemaining) || 0
    const isYellow = (it: any) => {
      const bq = boxQty(it)
      const qr = qtyRemaining(it)
      return bq > 0 && qr - bq > 0
    }
    const isRed = (it: any) => boxQty(it) === 0
    const isGreen = (it: any) => qtyRemaining(it) - boxQty(it) <= 0
    const sortedItems = [
      ...sortedByBin.filter(isYellow),
      ...sortedByBin.filter(it => isRed(it) && !isGreen(it)),
      ...sortedByBin.filter(isGreen)
    ]
    setAllItems(sortedItems)
    setItemsInSelectedBox(itemsInThisBox)
  }

  useEffect(() => {
    fetchExistingItems()
  }, [boxCode, selectedLocationId, selectedBatchLabel])

  useEffect(() => {
    if (!loading) {
      focusItemInput()
    }
  }, [loading])

  return (
    <>
      <Box
        sx={{
          margin: '20px',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Button
          variant='outlined'
          onClick={() => {
            setBoxCode('')
          }}
        >
          Back
        </Button>
        <Typography typography='h4' textAlign='center'>
          Box: {boxCode}
        </Typography>
      </Box>
      <Box onKeyDown={handleKeyDown} sx={{ margin: '50px 20px' }}>
        <TextField
          autoFocus
          inputRef={itemInputRef}
          label='Item Number'
          fullWidth
          value={itemCode}
          error={error}
          sx={{ marginBottom: '20px' }}
          helperText={error && 'This field cannot be empty'}
          onChange={e => {
            setError(false)
            setItemCode(e.target.value)
          }}
        />
        <Box sx={{ marginBottom: '20px' }}>
          <Typography variant='h6'>Items in this rack</Typography>
          {allItems.map((orderItem: OrderItem) => (
            <Box
              key={orderItem.name}
              sx={{
                backgroundColor: getBoxItemBackgroundColor(orderItem),
                justifyContent: 'space-between',
                padding: '10px',
                marginTop: '10px',
                borderRadius: '4px'
              }}
            >
              <Grid container justifyContent='space-between'>
                <Grid>
                  <Typography>Name: {orderItem.name}</Typography>
                  <Typography>Bin: {orderItem.bin}</Typography>
                  <Typography>Note: {orderItem.note}</Typography>
                </Grid>
                <Grid>
                  <Typography>
                    Quantity Needed: {orderItem.quantityRemaining}
                  </Typography>
                  <Typography>
                    Quantity Picked (this batch boxes):{' '}
                    {orderItem.boxQuantity || 0}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          ))}
        </Box>
        <Box>
          <Typography variant='h6'>Items in this box</Typography>
          {itemsInSelectedBox && itemsInSelectedBox.length > 0 ? (
            itemsInSelectedBox.map((item: any) => (
              <Box
                key={item.name}
                sx={{
                  backgroundColor: '#f0f0f0',
                  justifyContent: 'space-between',
                  padding: '10px',
                  marginTop: '10px',
                  borderRadius: '4px'
                }}
              >
                <Grid container justifyContent='space-between'>
                  <Grid>
                    <Typography>Name: {item.name}</Typography>
                  </Grid>
                  <Grid>
                    <Typography>Quantity in Box: {item.quantity}</Typography>
                  </Grid>
                </Grid>
              </Box>
            ))
          ) : (
            <Box>
              <Typography>No items in this box</Typography>
            </Box>
          )}
        </Box>
      </Box>
    </>
  )
}

export default ScanItem
